import React, { useCallback, useMemo, useState } from 'react';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { ClickAwayListener } from '@mui/material';
import { isEmpty } from 'lodash';
import { Link, useParams } from 'react-router-dom';
import { Firm } from 'api';
import { firmSummaryUrl, homePath } from 'common/config/urls';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { User } from 'common/types/user';
import { FirmLogo } from 'components/FirmLogo';
import { BottomMenuItems, TopMenuItems } from 'layouts/Main/components/Sidebar/components';
import { CompanyParams, SidebarProps } from './Sidebar.props';
import {
  DrawerBottom,
  DrawerContainer,
  DrawerTop,
  LogoContainer,
  SidebarBottom,
  SidebarTop,
  StyledDrawer,
  ToggleButton,
} from './Sidebar.styles';

const Sidebar = ({ onClose, className }: SidebarProps) => {
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { firmInfo, user } = storeValues as unknown as { firmInfo: Firm; user: User };
  const { firmSlugParam, tableSlugParam } = useParams<CompanyParams>();
  const [openSidebar, setOpenSidebar] = useState(false);
  const drawerWidth = (isOpen: boolean | undefined) => (isOpen ? 300 : 60);
  const width = drawerWidth(openSidebar);

  const handleLeave = () => {
    if (openSidebar) {
      setOpenSidebar(false);
    }
  };

  const home = useMemo(() => {
    if (firmInfo && !isEmpty(firmInfo)) {
      const { slug } = firmInfo;
      return firmSummaryUrl(slug ?? '');
    }
    return homePath;
  }, [firmInfo]);

  const superuserCompGroupsPageIsRendered = useMemo(
    () => user?.is_superuser && firmSlugParam === '0' && tableSlugParam === 'comp-groups',
    [user, firmSlugParam, tableSlugParam]
  );

  const toggleSidebar = useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      const target = event.target as HTMLDivElement;
      const currentTarget = event.currentTarget as HTMLDivElement;

      if (currentTarget.id === 'toggle-button') {
        setOpenSidebar(!openSidebar);
        return;
      }

      if (openSidebar && (target.closest('#sidebar-top') || target.closest('#sidebar-bottom'))) {
        return;
      }

      setOpenSidebar(!openSidebar);
    },
    [openSidebar]
  );

  return (
    <ClickAwayListener onClickAway={handleLeave}>
      <StyledDrawer
        open={openSidebar}
        width={width}
        variant="permanent"
        anchor="left"
        onClose={onClose}
        PaperProps={{
          elevation: openSidebar ? 16 : 1,
          style: { overflow: 'visible' },
        }}>
        <DrawerContainer className={className}>
          <DrawerTop>
            <LogoContainer square elevation={1}>
              <Link to={home} data-testid="logo">
                <FirmLogo firmInfo={firmInfo} isSidebarOpen={openSidebar} />
              </Link>
            </LogoContainer>
          </DrawerTop>
          <DrawerBottom
            onClick={toggleSidebar}
            tabIndex={0}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                toggleSidebar(event as unknown as React.MouseEvent<HTMLDivElement | HTMLButtonElement>);
              }
            }}
            role="button">
            <SidebarTop id="sidebar-top">
              <TopMenuItems
                open={openSidebar}
                setOpenSidebar={setOpenSidebar}
                renderFundsAndCompanies={!superuserCompGroupsPageIsRendered}
              />
            </SidebarTop>
            <SidebarBottom id="sidebar-bottom">
              <BottomMenuItems open={openSidebar} setOpenSidebar={setOpenSidebar} />
              <ToggleButton
                id="toggle-button"
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  toggleSidebar(e);
                }}>
                {openSidebar ? <ArrowBack /> : <ArrowForward />}
              </ToggleButton>
            </SidebarBottom>
          </DrawerBottom>
        </DrawerContainer>
      </StyledDrawer>
    </ClickAwayListener>
  );
};

export default Sidebar;
